import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Container,
  Paper,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PublicIcon from "@mui/icons-material/Public";

// ── Clue data ────────────────────────────────────────────────────────────────

const EASY_CLUES = [
  { number: 1,  clue: "What came first the chicken or this room?" },
  { number: 2,  clue: "We live in a society." },
  { number: 3,  clue: "Computer lab named for a simple machine, or the shape of some doorstops" },
  { number: 4,  clue: "Lookin for an Eng-y snack." },
  { number: 5,  clue: "Art's gift to Eng." },
  { number: 6,  clue: "They who speak for the tool, and undergrad engineering students." },
  { number: 7,  clue: "Engiqueers home base, near the Oteam homebase too!" },
  { number: 8,  clue: "Best view of RCH Courtyard." },
  { number: 9,  clue: "Concrete canoes bits stay on display next to a musical serenade." },
  { number: 10, clue: "A tree that's not green." },
  { number: 11, clue: "Where the confused FYs go to get less confused." },
  { number: 12, clue: "This will help you pass first year lowkey. Where COOPs help teach you your FY courses." },
  { number: 13, clue: "Where a COOP can help find the part you need." },
  { number: 14, clue: "Somewhere over the rainbow ion tracks..." },
  { number: 15, clue: "Design teams' home base." },
  { number: 16, clue: "It has a balcony?" },
  { number: 17, clue: "Where the youngins get building." },
  { number: 18, clue: "A really cool chem column." },
  { number: 19, clue: "Where CHE plays tunes." },
  { number: 20, clue: "Where ideas reach the 2nd level." },
  { number: 21, clue: "Where eng tea is spilled." },
  { number: 22, clue: "Where ECE students go to chill." },
  { number: 23, clue: "Eng student parts shop." },
  { number: 24, clue: "You break it, we fix it!" },
  { number: 25, clue: "SSSSSSSSSHHHHHHHHHHHHHHHHHH eng students working." },
  { number: 26, clue: "SYDE students also got mad musical skills." },
  { number: 27, clue: "Glowy purple sign for the linkedin." },
  { number: 28, clue: "Bridging MME to CHE." },
  { number: 29, clue: "Dungeon computer lab." },
  { number: 30, clue: "glub glub *eng* glub" },
];

const MEDIUM_CLUES = [
  { number: 31, clue: "Golden ticket not included." },
  { number: 32, clue: "Timbits in the underground." },
  { number: 33, clue: "Where math and eng students go to suffer study." },
  { number: 34, clue: "Sugarcube in the sky." },
  { number: 35, clue: "RAAAAAAAWRRRRRRRRR" },
  { number: 36, clue: "IT'S ALIVE! ITS- a wall?" },
  { number: 37, clue: "Overpriced macha where the Health Students hang out." },
  { number: 38, clue: "Campus' light rail transit." },
  { number: 39, clue: "Ben and Jerry's fridge on campus." },
  { number: 40, clue: "Garden that doesn't grow." },
  { number: 41, clue: "Big ahh metal pig." },
  { number: 42, clue: "Waterloo for the gram." },
  { number: 43, clue: "Where you can go for extra academic help." },
  { number: 44, clue: "Get a pump on between classes." },
  { number: 45, clue: "Climbing on campus." },
  { number: 46, clue: "Tiny, microscopic, miniscule basement instrument." },
  { number: 47, clue: "Where you go for your check ups on campus." },
  { number: 48, clue: "Uni bar." },
  { number: 49, clue: "Steps to unlock a door: 1. Put key in slot. 2. _______" },
  { number: 50, clue: "Every Science student's favourite spot to go between classes." },
];

const HARD_CLUES = [
  { number: 51, clue: "Best cookies in town!!" },
  { number: 52, clue: "Where live king crabs roam." },
  { number: 53, clue: "End on the ion line closest to us." },
  { number: 54, clue: "Bridging Wloo Campus to beautiful fields." },
  { number: 55, clue: "Going on a ___ run, anyone wanna come?" },
  { number: 56, clue: "For one and _____!" },
  { number: 57, clue: "Many galleries full of muck and shards." },
  { number: 58, clue: "Best record store in uptown Waterloo." },
  { number: 59, clue: "Oldest twin with great movies." },
  { number: 60, clue: "Be there or be ______." },
];

// ── Difficulty config ────────────────────────────────────────────────────────

const DIFFICULTIES = [
  {
    key: "easy",
    label: "Easy",
    subtitle: "Eng Campus",
    clues: EASY_CLUES,
    chipColor: "#2e7d32",   // green
    headerColor: "#1b5e20",
    bgColor: "#f1f8e9",
    borderColor: "#81c784",
    numberColor: "#2e7d32",
  },
  {
    key: "medium",
    label: "Medium",
    subtitle: "Overall Campus",
    clues: MEDIUM_CLUES,
    chipColor: "#e65100",   // orange
    headerColor: "#bf360c",
    bgColor: "#fff3e0",
    borderColor: "#ffb74d",
    numberColor: "#e65100",
  },
  {
    key: "hard",
    label: "Hard",
    subtitle: "Off Campus",
    clues: HARD_CLUES,
    chipColor: "#b71c1c",   // red
    headerColor: "#7f0000",
    bgColor: "#ffebee",
    borderColor: "#e57373",
    numberColor: "#b71c1c",
  },
];

// ── Sub-components ───────────────────────────────────────────────────────────

const ClueCard = ({ number, clue, numberColor, borderColor }) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: borderColor,
      borderRadius: 2,
      height: "100%",
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: `0 4px 14px ${borderColor}` },
    }}
  >
    <CardContent sx={{ pb: "12px !important" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: numberColor,
            minWidth: "2rem",
            lineHeight: 1.3,
          }}
        >
          {number}.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
          {clue}
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

const DifficultySection = ({ config, visible }) => {
  if (!visible) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        border: `2px solid ${config.borderColor}`,
        borderRadius: 3,
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          bgcolor: config.headerColor,
          color: "white",
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={config.label}
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: 700,
              fontSize: "0.85rem",
              border: "1px solid rgba(255,255,255,0.4)",
            }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {config.subtitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{ ml: "auto !important", opacity: 0.8 }}
          >
            {config.clues.length} clues
          </Typography>
        </Stack>
      </Box>

      {/* Clue grid */}
      <Box sx={{ bgcolor: config.bgColor, p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          {config.clues.map(({ number, clue }) => (
            <Grid item xs={12} sm={6} md={4} key={number}>
              <ClueCard
                number={number}
                clue={clue}
                numberColor={config.numberColor}
                borderColor={config.borderColor}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const QrHunt = () => {
  const [filter, setFilter] = useState("all");

  const handleFilter = (_e, newFilter) => {
    if (newFilter !== null) setFilter(newFilter);
  };

  const totalClues =
    EASY_CLUES.length + MEDIUM_CLUES.length + HARD_CLUES.length;

  return (
    <Box>
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: "#5D0096",
          color: "white",
          borderRadius: 0,
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} justifyContent="center" alignItems="center" textAlign="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <SearchIcon sx={{ fontSize: "2.5rem", opacity: 0.85 }} />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "2.2rem", md: "3rem" },
                  textShadow: "2px 2px 4px rgba(0,0,0,0.25)",
                }}
              >
                QR Code Hunt
              </Typography>
              <SearchIcon sx={{ fontSize: "2.5rem", opacity: 0.85 }} />
            </Stack>

            <Typography
              variant="h5"
              sx={{ maxWidth: 780, lineHeight: 1.8, opacity: 0.95 }}
            >
              Scan QR codes hidden around campus to score points for your team!
              Use these clues to track them all down.
            </Typography>

            {/* Stat chips */}
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
              {[
                { icon: <LockOpenIcon fontSize="small" />, label: `${totalClues} total clues` },
                { icon: <PublicIcon fontSize="small" />, label: "3 difficulty tiers" },
              ].map(({ icon, label }) => (
                <Chip
                  key={label}
                  icon={icon}
                  label={label}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.3)",
                    "& .MuiChip-icon": { color: "white" },
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    px: 1,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Container>
      </Paper>

      {/* Body */}
      <Container maxWidth="lg" sx={{ pb: 6 }}>

        {/* How to play */}
        <Paper elevation={2} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            How to Play
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[
              { step: "1", text: "Read a clue and figure out where the QR code is hidden." },
              { step: "2", text: "Head to the location and scan the QR code with your phone's camera." },
              { step: "3", text: "You'll be taken to the website where your scan is recorded and points added to your team." },
              { step: "4", text: "Harder clues are worth more points — but they're trickier to find!" },
            ].map(({ step, text }) => (
              <Grid item xs={12} sm={6} key={step}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      bgcolor: "#5D0096",
                      color: "white",
                      borderRadius: "50%",
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "0.85rem",
                      flexShrink: 0,
                      mt: "2px",
                    }}
                  >
                    {step}
                  </Box>
                  <Typography variant="body1">{text}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Filter toggle */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Typography variant="body1" fontWeight={600}>
            Show:
          </Typography>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilter}
            size="small"
            sx={{
              "& .MuiToggleButton-root.Mui-selected": {
                bgcolor: "#5D0096",
                color: "white",
                "&:hover": { bgcolor: "#3F0066" },
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="easy" sx={{ color: "#2e7d32", fontWeight: 700 }}>Easy</ToggleButton>
            <ToggleButton value="medium" sx={{ color: "#e65100", fontWeight: 700 }}>Medium</ToggleButton>
            <ToggleButton value="hard" sx={{ color: "#b71c1c", fontWeight: 700 }}>Hard</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Clue sections */}
        {DIFFICULTIES.map((config) => (
          <DifficultySection
            key={config.key}
            config={config}
            visible={filter === "all" || filter === config.key}
          />
        ))}
      </Container>
    </Box>
  );
};

export default QrHunt;
